import React, { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import PropTypes, { InferProps } from 'prop-types';

import classnames from 'classnames/bind';

import GenerationSteps from '@CONSTANTS/GENERATION_STEPS.constant';
import ROUTES from '@CONSTANTS/ROUTES.constant';

import BasicButton from '@COMPONENTS/COMMON/buttons/BasicButton';

import { GenerationMode } from '@PAGES/manager/options/Options.page';
import useDocumentGenerationSteps from '@HOOKS/store/useDocumentGenerationSteps';
import { OptionsStep } from '@REDUCERS';

import styles from './StepNavigation.module.scss';

const cx: CX = classnames.bind(styles);

// Define the base step order for navigation
const BASE_STEP_ORDER = [
    GenerationSteps.DOCUMENT,
    GenerationSteps.TYPE,
    GenerationSteps.BRAND,
    GenerationSteps.LOCATION,
    GenerationSteps.OPTIONS,
    GenerationSteps.DOWNLOAD_DOCUMENT,
];

const STEP_ROUTES: Record<GenerationSteps, string> = {
    [GenerationSteps.DOCUMENT]: ROUTES.chooseDocument.path,
    [GenerationSteps.TYPE]: ROUTES.type.path,
    [GenerationSteps.BRAND]: ROUTES.brand.path,
    [GenerationSteps.LOCATION]: ROUTES.location.path,
    [GenerationSteps.OPTIONS]: ROUTES.options.path,
    [GenerationSteps.SELECT_CONTENT]: ROUTES.selectContent.path,
    [GenerationSteps.DOWNLOAD_DOCUMENT]: ROUTES.downloadDocument.path,
};

function StepNavigation(props: Props) {
    const {
        onNext,
        nextDisabled,
        nextText,
        showNext,
        nextVariant,
        className,
        sticky = false,
    } = props;

    const navigate = useNavigate();
    const location = useLocation();
    
    const { services } = useDocumentGenerationSteps();
    const optionsStep: OptionsStep = services.getStepData(GenerationSteps.OPTIONS);
    
    // Determine if SELECT_CONTENT should be included based on partial extraction mode
    const shouldIncludeSelectContent = optionsStep?.data?.id === GenerationMode.PARTIAL;
    const STEP_ORDER = shouldIncludeSelectContent 
        ? [
            GenerationSteps.DOCUMENT,
            GenerationSteps.TYPE,
            GenerationSteps.BRAND,
            GenerationSteps.LOCATION,
            GenerationSteps.OPTIONS,
            GenerationSteps.SELECT_CONTENT,
            GenerationSteps.DOWNLOAD_DOCUMENT,
        ]
        : BASE_STEP_ORDER;

    const currentStep = location.pathname as GenerationSteps;
    const currentIndex = STEP_ORDER.indexOf(currentStep);
    
    const isFirstStep = currentIndex === 0;
    const isLastStep = currentIndex === STEP_ORDER.length - 1;
    
    const previousStep = !isFirstStep ? STEP_ORDER[currentIndex - 1] : null;

    const handleBack = () => {
        if (previousStep && STEP_ROUTES[previousStep]) {
            const previousRoute = STEP_ROUTES[previousStep];
            navigate(previousRoute);
        }
    };

    const handleNext = () => {
        if (onNext) {
            onNext();
        }
    };

    useEffect(() => {
        if (sticky) {
            document.body.classList.add('sticky-footer');
            return () => {
                document.body.classList.remove('sticky-footer');
            };
        }
    }, [sticky]);

    return (
        <div className={cx('step-navigation', className, { 'with-sidebar': sticky })}>
            <div className={cx('nav-container')}>
                <div className={cx('back-section')}>
                    {!isFirstStep && (
                        <BasicButton
                            title="Back"
                            variant="secondary"
                            size="medium"
                            onClick={handleBack}
                        />
                    )}
                </div>

                <div className={cx('next-section')}>
                    {showNext && !isLastStep && (
                        <BasicButton
                            title={nextText || 'Next Step'}
                            variant="primary"
                            size="medium"
                            onClick={handleNext}
                            locked={!!nextDisabled}
                        />
                    )}
                </div>
            </div>
        </div>
    );
}

StepNavigation.defaultProps = {
    nextDisabled: false,
    nextText: 'Next Step',
    showNext: true,
    nextVariant: 'primary',
    className: '',
    sticky: false,
};

StepNavigation.propTypes = {
    onNext: PropTypes.func.isRequired,
    nextDisabled: PropTypes.bool,
    nextText: PropTypes.string,
    showNext: PropTypes.bool,
    nextVariant: PropTypes.oneOf(['primary', 'secondary', 'success', 'warning', 'danger', 'neutral']),
    className: PropTypes.string,
    sticky: PropTypes.bool,
};

type Props = InferProps<typeof StepNavigation.propTypes>;

export default StepNavigation; 